'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
    name: string;
}

interface AdvantagesSectionProps {
    plan: Plan;
    translationNamespace: string;
    advantageKeys: string[];
    gridCols?: '2' | '3';
    hasSummary?: boolean;
    hasAdditionalDescription?: boolean;
    sectionVariant?: 'default' | 'gradient';
}

export function AdvantagesSection({ 
    plan, 
    translationNamespace, 
    advantageKeys,
    gridCols = '3',
    hasSummary = false,
    hasAdditionalDescription = false,
    sectionVariant = 'default'
}: AdvantagesSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const advantages = advantageKeys.map(key => t(key)).filter(Boolean);

    const gridClass = {
        '2': 'md:grid-cols-2',
        '3': 'md:grid-cols-2 lg:grid-cols-3',
    }[gridCols] || 'md:grid-cols-2 lg:grid-cols-3';

    const sectionClassName = sectionVariant === 'gradient' 
        ? 'relative bg-white dark:bg-gray-950' 
        : 'relative bg-white dark:bg-gray-950';

    return (
        <SectionWrapper ref={sectionRef.ref} variant={sectionVariant} padding="md" className={sectionClassName}>
            <div className="container">
                <div className={cn(
                    `mb-12 transition-all duration-700 ${hasSummary ? 'text-center' : ''}`,
                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                )}>
                    <Typography variant="h2" className="mb-4 text-gray-900 dark:text-white">
                        {t('title')}
                    </Typography>
                    {hasSummary && (
                        <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {t('summary')}
                        </Typography>
                    )}
                </div>

                <div className={`grid grid-cols-1 ${gridClass} gap-4 ${hasAdditionalDescription ? 'max-w-5xl mx-auto' : ''}`}>
                    {advantages.map((advantage: string, index: number) => (
                        <div
                            key={index}
                            className={cn(
                                "bg-gray-100 dark:bg-gray-800 rounded-xl p-5 flex items-center gap-4 transition-all duration-700",
                                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            )}
                            style={{ transitionDelay: `${200 + index * 50}ms` }}
                        >
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                                <CheckCircle2 className="w-6 h-6 text-white" />
                            </div>
                            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                {advantage}
                            </Typography>
                        </div>
                    ))}
                </div>

                {hasAdditionalDescription && (
                    <Typography 
                        variant="p" 
                        className={cn(
                            "mt-10 text-lg max-w-4xl mx-auto text-center transition-all duration-700",
                            sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                        )}
                        style={{ transitionDelay: `${400 + advantages.length * 50}ms` }}
                    >
                        {t('additionalDescription')}
                    </Typography>
                )}
            </div>
        </SectionWrapper>
    );
}

